import axios from 'axios';

// Determine API base URL - works for both local development and production
const getApiBaseUrl = () => {
  // If explicitly set via environment variable, use that
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // For local development, use the backend port
  if (process.env.NODE_ENV === 'development') {
    return 'http://localhost:8000/api/v1';
  }
  
  // For production, assume API is served from same host but different port or path
  // This will be overridden by REACT_APP_API_URL in actual deployments
  return '/api/v1';
};

const API_BASE_URL = getApiBaseUrl();

// Export the base URL function for use in other components
export { getApiBaseUrl };

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor — when the 30-min JWT expires mid-session, the backend
// returns 401. Clear the stored token and send the user to /login so they can
// re-authenticate instead of silently failing every subsequent request. The
// login endpoint is exempted so invalid-credentials errors surface on the form
// (and to avoid a redirect loop). The error is re-rejected so existing catch
// blocks still run.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const requestUrl = error.config?.url || '';
    const isLoginRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/login');
    if (error.response?.status === 401 && !isLoginRequest) {
      localStorage.removeItem('authToken');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Classification Metadata — levels, compartments (with categories), dissemination controls, countries, groups
export const getClassificationMetadata = async () => {
  const response = await api.get('/classification/metadata');
  return response.data;
};

// Document Classification
export const classifyDocument = async (text, useScgRules = false) => {
  const endpoint = useScgRules ? '/classify/scg' : '/classify';
  const response = await api.post(endpoint, { text, use_scg_rules: useScgRules });
  return response.data;
};

// Enhanced Document Classification with advanced retrieval
export const classifyDocumentEnhanced = async (text, options = {}) => {
  const {
    useScgRules = false,
    useHyde = true,
    useSemanticRetrieval = true,
    retrievalMode = null
  } = options;

  const body = {
    text,
    use_scg_rules: useScgRules,
    use_hyde: useHyde,
    use_semantic_retrieval: useSemanticRetrieval
  };
  if (retrievalMode) body.retrieval_mode = retrievalMode;

  const response = await api.post('/classify', body);
  return response.data;
};

// Paragraph-level Classification
export const classifyParagraphs = async (text, options = {}) => {
  const {
    chunkSize = 1000,
    chunkOverlap = 100,
    useScgRules = false,
    retrievalMode = null
  } = options;

  const body = {
    text,
    chunk_size: chunkSize,
    chunk_overlap: chunkOverlap,
    use_scg_rules: useScgRules
  };
  if (retrievalMode) body.retrieval_mode = retrievalMode;

  const response = await api.post('/classify/paragraphs', body);
  return response.data;
};

// Classification Review - Review pre-marked documents for accuracy
export const reviewClassificationMarkings = async (text, options = {}) => {
  const {
    useScgRules = true,
    useHyde = true,  // Enable HyDE for better semantic bridging
    useSemanticRetrieval = true
  } = options;

  const response = await api.post('/classify/review', {
    text,
    use_scg_rules: useScgRules,
    use_hyde: useHyde,
    use_semantic_retrieval: useSemanticRetrieval
  });
  return response.data;
};

// Upload and Review Classification - Upload a pre-marked document and review accuracy
export const uploadAndReviewClassification = async (file, options = {}) => {
  const {
    useScgRules = true,
    useHyde = true,  // Enable HyDE for better semantic bridging
    useSemanticRetrieval = true
  } = options;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('use_scg_rules', useScgRules.toString());
  formData.append('use_hyde', useHyde.toString());
  formData.append('use_semantic_retrieval', useSemanticRetrieval.toString());

  const response = await api.post('/upload/review', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Document Upload
export const uploadDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Upload and Classify Document with Portion Marks
export const uploadAndClassifyParagraphs = async (file, options = {}) => {
  const {
    useScgRules = true,
    chunkSize = 1500,
    useLlmSegmentation = true,
    retrievalMode = null,
    onUploadProgress = null
  } = options;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('use_scg_rules', useScgRules.toString());
  formData.append('chunk_size', chunkSize.toString());
  formData.append('use_llm_segmentation', useLlmSegmentation.toString());
  if (retrievalMode) formData.append('retrieval_mode', retrievalMode);

  const response = await api.post('/upload/classify-paragraphs', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...(onUploadProgress && { onUploadProgress }),
  });
  return response.data;
};

// Bulk Classification - upload multiple files at once
export const classifyBulk = async (files) => {
  const formData = new FormData();
  files.forEach(f => formData.append('files', f));
  const response = await api.post('/classify/bulk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Bulk Classification - poll batch status
export const getBulkStatus = async (batchId) => {
  const response = await api.get(`/classify/bulk/${batchId}`);
  return response.data;
};

// Upload and generate formatted document with portion marks
export const uploadAndClassifyFormatted = async (file, options = {}) => {
  const {
    useScgRules = true,
    chunkSize = 1500,
    retrievalMode = null
  } = options;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('use_scg_rules', useScgRules.toString());
  formData.append('chunk_size', chunkSize.toString());
  if (retrievalMode) formData.append('retrieval_mode', retrievalMode);
  
  const response = await api.post('/upload/classify-formatted', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob', // Important for binary file downloads
  });
  return response;
};

// Format document using existing classification results (no re-classification)
export const formatClassifiedDocument = async (file, classificationData) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('classification_data', JSON.stringify(classificationData));
  
  const response = await api.post('/format-document', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob', // Important for binary file downloads
  });
  return response;
};

// SCG Management
export const uploadSCGDocument = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await api.post('/scg/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getSCGRules = async () => {
  const response = await api.get('/scg/rules');
  return response.data;
};

// SCG Rule Management
export const searchSCGRules = async (query = "", classificationLevel = "", source = "", limit = 50, offset = 0) => {
  const params = new URLSearchParams();
  if (query) params.append('query', query);
  if (classificationLevel) params.append('classification_level', classificationLevel);
  if (source) params.append('source', source);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  
  const response = await api.get(`/scg/search?${params.toString()}`);
  return response.data;
};

export const getSCGSources = async () => {
  const response = await api.get('/scg/sources');
  return response.data;
};

export const getSCGRule = async (ruleId) => {
  const response = await api.get(`/scg/rule/${ruleId}`);
  return response.data;
};

export const updateSCGRule = async (ruleId, updateData) => {
  const response = await api.put(`/scg/rule/${ruleId}`, updateData);
  return response.data;
};

export const createSCGRule = async (ruleData) => {
  const response = await api.post('/scg/rule', ruleData);
  return response.data;
};

export const deleteSCGRule = async (ruleId) => {
  const response = await api.delete(`/scg/rule/${ruleId}`);
  return response.data;
};

export const deleteSCGRulesBulk = async (ruleIds) => {
  const response = await api.post('/scg/delete/bulk', { rule_ids: ruleIds });
  return response.data;
};

export const deleteSCGRulesBySource = async (sourceFile) => {
  const response = await api.delete(`/scg/source/${encodeURIComponent(sourceFile)}`);
  return response.data;
};

// Re-index all SCG rules with current embedding model
export const reindexSCGRules = async () => {
  const response = await api.post('/scg/reindex');
  return response.data;
};

// Re-index all FOIA rules with current embedding model
export const reindexFOIARules = async () => {
  const response = await api.post('/foia/reindex');
  return response.data;
};

// Adversarial Self-Test
export const runAdversarialTest = async (data) => {
  const response = await api.post('/defense/classify/adversarial-test', data);
  return response.data;
};

// SCG Source Information
export const getAllRejectedSCGRules = async (limit = 2000) => {
  const response = await api.get(`/scg/rejected-rules?limit=${limit}`);
  return response.data;
};

export const getSCGRuleSourceInfo = async (ruleId) => {
  const response = await api.get(`/scg/rule/${ruleId}/source`);
  return response.data;
};

export const getSCGPdfUrl = (fileHash, page = null) => {
  // For Docker/production, use relative URL to avoid localhost port issues
  // For development, use the full localhost:8000 URL
  let baseUrl;
  if (process.env.NODE_ENV === 'development') {
    baseUrl = `${API_BASE_URL}/scg/pdf/${fileHash}`;
  } else {
    // Use relative URL for Docker/production to work with nginx proxy
    baseUrl = `/api/v1/scg/pdf/${fileHash}`;
  }
  return page ? `${baseUrl}?page=${page}` : baseUrl;
};

// Document Search
export const searchDocuments = async (query, topK = 10, filters = null, rewriteQuery = false) => {
  const response = await api.post('/search', {
    query,
    top_k: topK,
    filters,
    rewrite_query: rewriteQuery
  });
  return response.data;
};

// List Documents
export const listDocuments = async (limit = 50) => {
  const response = await api.get(`/documents?limit=${limit}`);
  return response.data;
};

// Generate Embedding
export const generateEmbedding = async (text) => {
  const response = await api.post('/embed', { text });
  return response.data;
};

// Query Rewriting
export const rewriteQuery = async (query) => {
  const response = await api.post(`/query/rewrite?query=${encodeURIComponent(query)}`);
  return response.data;
};

// Health Check
export const healthCheck = async () => {
  const response = await axios.get('/health'); // Use direct call, not through api instance
  return response.data;
};

// Tearline Document Sanitization
export const sanitizeDocument = async (text, targetClassification, targetCaveats = [], targetReleasability = [], maxIterations = 5, preClassifiedDocument = null) => {
  const response = await api.post('/tearline/sanitize', {
    text,
    target_classification: targetClassification,
    target_caveats: targetCaveats,
    target_releasability: targetReleasability,
    max_iterations: maxIterations,
    sanitization_methods: ["generalization", "redaction", "obfuscation", "paraphrasing", "abstraction", "temporal_shifting", "geographical_masking", "entity_anonymization", "technical_simplification"],
    pre_classified_document: preClassifiedDocument
  });
  return response.data;
};

// Streaming Tearline Document Sanitization
export const sanitizeDocumentStream = async (text, targetClassification, targetCaveats = [], targetReleasability = [], maxIterations = 5, onProgress, preClassifiedDocument = null, sanitizationMode = 'standard', retrievalMode = null) => {
  return new Promise((resolve, reject) => {
    // Get auth token for authenticated request
    const authToken = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
    };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // First, start the sanitization process
    fetch(`${API_BASE_URL}/tearline/sanitize/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        text,
        target_classification: targetClassification,
        target_caveats: targetCaveats,
        target_releasability: targetReleasability,
        max_iterations: maxIterations,
        sanitization_methods: ["generalization", "redaction", "obfuscation", "paraphrasing", "abstraction", "temporal_shifting", "geographical_masking", "entity_anonymization", "technical_simplification"],
        pre_classified_document: preClassifiedDocument,
        sanitization_mode: sanitizationMode,
        ...(retrievalMode && { retrieval_mode: retrievalMode })
      }),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalResult = null;

      function readStream() {
        return reader.read().then(({ done, value }) => {
          if (done) {
            resolve(finalResult);
            return;
          }
          
          buffer += decoder.decode(value, { stream: true });
          
          // Process complete lines
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                if (onProgress) {
                  setTimeout(() => {
                    try {
                      onProgress(data);
                    } catch (error) {
                      console.error('Error in onProgress callback:', error);
                    }
                  }, 0);
                }

                if (data.final_result) {
                  finalResult = data.final_result;
                }
              } catch (e) {
                // Failed to parse streaming data line
              }
            }
          }
          
          // Continue reading
          return readStream();
        });
      }
      
      return readStream();
    })
    .catch(error => {
      console.error('API: Streaming request failed:', error);
      reject(error);
    });
  });
};

// Testing and Optimization APIs
export const startTestRun = async (testType = 'classification', testQueries = null, configParams = null, retrievalMode = null) => {
  const payload = {
    test_type: testType,
    test_queries: testQueries,
    config_params: configParams
  };
  if (retrievalMode) {
    payload.retrieval_mode = retrievalMode;
  }
  const response = await api.post('/testing/run', payload);
  return response.data;
};

export const getTestRunStatus = async (testId) => {
  const response = await api.get(`/testing/run/${testId}`);
  return response.data;
};

export const startConfigOptimization = async (optimizationParams, testQueries = null, scoringWeights = null) => {
  const response = await api.post('/testing/optimize', {
    test_queries: testQueries,
    optimization_params: optimizationParams,
    scoring_weights: scoringWeights
  });
  return response.data;
};

export const getOptimizationStatus = async (optimizationId) => {
  const response = await api.get(`/testing/optimize/${optimizationId}`);
  return response.data;
};

export const getDefaultTestQueries = async () => {
  const response = await api.get('/testing/default-queries');
  return response.data;
};

export const getConfigOptimizationTemplates = async () => {
  const response = await api.get('/testing/config-templates');
  return response.data;
};

export const getTestHistory = async () => {
  const response = await api.get('/testing/history');
  return response.data;
};

export const getTestHistoryResult = async (filename) => {
  const response = await api.get(`/testing/history/${filename}`);
  return response.data;
};

// ==================== FOIA PROCESSING APIs ====================

// FOIA Document Processing
export const processFOIADocument = async (text, agency = '', requesterInterest = '', useHyde = true) => {
  const response = await api.post('/foia/process', {
    text,
    agency,
    requester_interest: requesterInterest,
    use_hyde: useHyde
  });
  return response.data;
};

// FOIA File Processing
export const processFOIAFile = async (file, agency = '', requesterInterest = '', useHyde = true) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);
  formData.append('use_hyde', useHyde);
  
  const response = await api.post('/foia/process/file', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// FOIA Rule Management
export const uploadFOIAGuidance = async (file, agency = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  
  const response = await api.post('/foia/rules/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const searchFOIARules = async (query = '', exemptionCategory = '', source = '', limit = 50, offset = 0) => {
  const params = new URLSearchParams({
    query,
    exemption_category: exemptionCategory,
    source,
    limit: limit.toString(),
    offset: offset.toString()
  });
  
  const response = await api.post('/foia/rules/search', {
    query,
    exemption_category: exemptionCategory,
    source,
    limit,
    offset
  });
  return response.data;
};

export const getFOIASources = async () => {
  const response = await api.get('/foia/rules/sources');
  return response.data;
};

export const getFOIAExemptions = async () => {
  const response = await api.get('/foia/rules/exemptions');
  return response.data;
};

export const getFOIARule = async (ruleId) => {
  const response = await api.get(`/foia/rules/${ruleId}`);
  return response.data;
};

export const deleteFOIARule = async (ruleId) => {
  const response = await api.delete(`/foia/rules/${ruleId}`);
  return response.data;
};

export const deleteFOIARulesBulk = async (ruleIds) => {
  const response = await api.post('/foia/rules/delete/bulk', {
    rule_ids: ruleIds
  });
  return response.data;
};

export const deleteFOIARulesBySource = async (sourceFile) => {
  const response = await api.delete(`/foia/rules/source/${encodeURIComponent(sourceFile)}`);
  return response.data;
};

// Enhanced FOIA Processing (Standard Exemptions + Rule-based + Conflict Detection)
export const processEnhancedFOIA = async (text, agency = '', requesterInterest = '', useHyde = true, conflictResolution = 'merge') => {
  const response = await api.post('/foia/process/enhanced', {
    text,
    agency,
    requester_interest: requesterInterest,
    use_hyde: useHyde,
    conflict_resolution: conflictResolution
  });
  return response.data;
};

// Enhanced FOIA Processing with Streaming
export const processEnhancedFOIAStream = async (text, agency = '', requesterInterest = '', useHyde = true) => {
  const response = await api.post('/foia/process/enhanced/stream', {
    text,
    agency,
    requester_interest: requesterInterest,
    use_hyde: useHyde
  });
  return response.data;
};

// PDF FOIA Redaction - Returns visually redacted PDF with full analysis
export const redactPDFForFOIA = async (file, agency = '', requesterInterest = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);
  
  const response = await api.post('/foia/redact/pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  });
  
  // Convert base64 PDF data to blob for download
  const pdfData = response.data.pdfData;
  const binaryString = atob(pdfData);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const pdfBlob = new Blob([bytes], { type: 'application/pdf' });
  
  return {
    pdfBlob: pdfBlob,
    filename: response.data.filename,
    redactionCount: response.data.redactionCount,
    exemptionsUsed: response.data.exemptionsUsed,
    processingTime: response.data.processingTime,
    confidenceScore: response.data.confidenceScore,
    mappingStats: response.data.mappingStats,
    textAnalysis: response.data.textAnalysis  // Full analysis data
  };
};

// PDF FOIA Analysis - Returns detailed analysis only
export const analyzePDFForFOIA = async (file, agency = '', requesterInterest = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);
  
  const response = await api.post('/foia/analyze/pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
};

// FOIA Document Discovery
export const discoverFOIADocuments = async (foiaRequestText, sourcePaths, options = {}) => {
  const requestBody = {
    foia_request_text: foiaRequestText,
    source_paths: sourcePaths,
    agency: options.agency || '',
    requester: options.requester || '',
    max_file_size_mb: options.maxFileSizeMb || 50,
    relevance_threshold: options.relevanceThreshold || 0.3,
  };

  // Add S3 credentials if provided
  if (options.s3AccessKeyId) {
    requestBody.s3_access_key_id = options.s3AccessKeyId;
  }
  if (options.s3SecretAccessKey) {
    requestBody.s3_secret_access_key = options.s3SecretAccessKey;
  }
  if (options.s3Region) {
    requestBody.s3_region = options.s3Region;
  }

  // Add M365 credentials if provided
  if (options.m365TenantId) {
    requestBody.m365_tenant_id = options.m365TenantId;
  }
  if (options.m365ClientId) {
    requestBody.m365_client_id = options.m365ClientId;
  }
  if (options.m365ClientSecret) {
    requestBody.m365_client_secret = options.m365ClientSecret;
  }

  const response = await api.post('/foia/discover', requestBody);
  return response.data;
};

// FOIA Document Discovery with streaming progress
export const discoverFOIADocumentsStream = (foiaRequestText, sourcePaths, options = {}, callbacks = {}) => {
  const { onProgress, onComplete, onError } = callbacks;
  const baseUrl = getApiBaseUrl();

  // Create request body
  const requestBody = {
    foia_request_text: foiaRequestText,
    source_paths: sourcePaths,
    agency: options.agency || '',
    requester: options.requester || '',
    max_file_size_mb: options.maxFileSizeMb || 50,
    relevance_threshold: options.relevanceThreshold || 0.3,
  };

  // Add S3 credentials if provided
  if (options.s3AccessKeyId) {
    requestBody.s3_access_key_id = options.s3AccessKeyId;
  }
  if (options.s3SecretAccessKey) {
    requestBody.s3_secret_access_key = options.s3SecretAccessKey;
  }
  if (options.s3Region) {
    requestBody.s3_region = options.s3Region;
  }

  // Add M365 credentials if provided
  if (options.m365TenantId) {
    requestBody.m365_tenant_id = options.m365TenantId;
  }
  if (options.m365ClientId) {
    requestBody.m365_client_id = options.m365ClientId;
  }
  if (options.m365ClientSecret) {
    requestBody.m365_client_secret = options.m365ClientSecret;
  }

  const body = JSON.stringify(requestBody);

  // Use fetch for SSE since axios doesn't support streaming well
  const controller = new AbortController();

  const fetchPromise = fetch(`${baseUrl}/foia/discover/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
    },
    body,
    signal: controller.signal,
  }).then(async (response) => {
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'progress' && onProgress) {
              onProgress(data);
            } else if (data.type === 'complete' && onComplete) {
              onComplete(data);
            } else if (data.type === 'error' && onError) {
              onError(new Error(data.error));
            } else if (data.error && onError) {
              onError(new Error(data.error));
            }
          } catch (e) {
            console.warn('Failed to parse SSE data:', line, e);
          }
        }
      }
    }
  }).catch((error) => {
    if (error.name !== 'AbortError' && onError) {
      onError(error);
    }
  });

  // Return cancel function
  return {
    cancel: () => controller.abort(),
    promise: fetchPromise,
  };
};

// Fetch a file discovered during FOIA Discovery for processing
export const getDiscoveryFile = async (filePath) => {
  const response = await api.get('/foia/discovery/file', {
    params: { file_path: filePath },
    responseType: 'blob',
  });
  return response.data;
};

// Model Management (Legacy endpoints for backward compatibility)
export const getAvailableModels = async () => {
  const response = await api.get('/config/models/available');
  return response.data;
};

export const getCurrentModel = async () => {
  const response = await api.get('/config/models/current');
  return response.data;
};

export const validateModelConfig = async (provider, modelId) => {
  const response = await api.post('/config/models/validate', null, {
    params: { provider, model_id: modelId }
  });
  return response.data;
};

export const switchModel = async (provider, modelId) => {
  const response = await api.post('/config/models/switch', null, {
    params: { provider, model_id: modelId }
  });
  return response.data;
};

export const clearModelCache = async () => {
  const response = await api.post('/config/models/cache/clear');
  return response.data;
};

// ==================== LiteLLM Model Configuration APIs ====================

// Get list of all supported LLM providers
export const getLLMProviders = async () => {
  const response = await api.get('/models/providers');
  return response.data;
};

// Get available models with full metadata (supports 100+ providers)
export const getLLMModelsWithInfo = async (provider = null) => {
  const params = provider ? { provider } : {};
  const response = await api.get('/models/available', { params });
  return response.data;
};

// Get simple model list (just IDs)
export const getLLMModelsSimple = async (provider = null) => {
  const params = provider ? { provider } : {};
  const response = await api.get('/models/available/simple', { params });
  return response.data;
};

// Get all per-function model configurations
export const getFunctionConfigs = async () => {
  const response = await api.get('/models/configs');
  return response.data;
};

// Get model configuration for a specific function
export const getFunctionConfig = async (functionName) => {
  const response = await api.get(`/models/configs/${functionName}`);
  return response.data;
};

// Update model configuration for a specific function
export const updateFunctionConfig = async (functionName, config) => {
  const response = await api.put(`/models/configs/${functionName}`, config);
  return response.data;
};

// Delete/reset a function's configuration
export const deleteFunctionConfig = async (functionName) => {
  const response = await api.delete(`/models/configs/${functionName}`);
  return response.data;
};

// Reset all function configurations to defaults
export const resetAllFunctionConfigs = async () => {
  const response = await api.post('/models/configs/reset');
  return response.data;
};

// Test a model configuration
export const testModelConfig = async (modelId, testPrompt = "Hello, please respond with a brief greeting.", temperature = 0.7, maxTokens = 100) => {
  const response = await api.post('/models/test', {
    model_id: modelId,
    test_prompt: testPrompt,
    temperature: temperature,
    max_tokens: maxTokens
  });
  return response.data;
};

// Get current active model info
export const getCurrentModelInfo = async () => {
  const response = await api.get('/models/current');
  return response.data;
};

// Switch the global model at runtime
export const switchLLMModel = async (provider, modelId) => {
  const params = { provider, model_id: modelId };
  const response = await api.post('/models/switch', null, { params });
  return response.data;
};

// Get list of functions that support model configuration
export const getSupportedFunctions = async () => {
  const response = await api.get('/models/functions');
  return response.data;
};

// ==================== MOSAIC DETECTION APIs ====================

// Analyze documents for mosaic effect risks
export const analyzeMosaicRisk = async (documents) => {
  const response = await api.post('/mosaic/analyze', { documents });
  return response.data;
};

// Check if adding a new document creates mosaic risks
export const checkMosaicAdditionRisk = async (newDocument, existingDocuments = []) => {
  const response = await api.post('/mosaic/check-addition', {
    new_document: newDocument,
    existing_documents: existingDocuments
  });
  return response.data;
};

// Check if tearline output has mosaic risks
export const checkTearlineMosaicRisk = async (originalText, sanitizedText, previouslyReleasedTexts = []) => {
  const response = await api.post('/mosaic/check-tearline', {
    original_text: originalText,
    sanitized_text: sanitizedText,
    previously_released_texts: previouslyReleasedTexts
  });
  return response.data;
};

// Clear the model cache
export const clearLLMModelCache = async () => {
  const response = await api.post('/models/cache/clear');
  return response.data;
};

// Continue Tearline Sanitization Stream  
export const continueSanitizeDocumentStream = async (targetClassification, targetCaveats = [], targetReleasability = [], maxIterations = 5, previousResult, onProgress, sanitizationMode = 'standard', retrievalMode = null) => {
  return new Promise((resolve, reject) => {
    // Get auth token for authenticated request
    const authToken = localStorage.getItem('authToken');
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Start the continue sanitization process
    fetch(`${API_BASE_URL}/tearline/continue/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        target_classification: targetClassification,
        target_caveats: targetCaveats,
        target_releasability: targetReleasability,
        max_iterations: maxIterations,
        previous_result: previousResult,
        sanitization_mode: sanitizationMode,
        ...(retrievalMode && { retrieval_mode: retrievalMode })
      }),
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const readStream = () => {
        reader.read().then(({ done, value }) => {
          if (done) {
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          lines.forEach(line => {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));

                if (onProgress) {
                  onProgress(data);
                }

                if (data.status === 'completed') {
                  resolve(data.final_result);
                } else if (data.status === 'error') {
                  reject(new Error(data.message || data.error || 'Continue sanitization failed'));
                }
              } catch (parseError) {
                // Failed to parse continue stream data
              }
            }
          });

          readStream();
        }).catch(error => {
          reject(error);
        });
      };

      readStream();
    })
    .catch(error => {
      reject(error);
    });
  });
};

// Tearline Upload Stream — upload a file (PDF/DOCX/PPTX/TXT) and stream tearline sanitization
export const tearlineUploadStream = async (file, targetClassification, targetCaveats = [], targetReleasability = [], maxIterations = 5, onProgress, sanitizationMode = 'standard', retrievalMode = null) => {
  return new Promise((resolve, reject) => {
    const authToken = localStorage.getItem('authToken');
    const headers = { 'Accept': 'text/event-stream' };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('target_classification', targetClassification);
    formData.append('target_caveats', JSON.stringify(targetCaveats));
    formData.append('target_releasability', JSON.stringify(targetReleasability));
    formData.append('max_iterations', maxIterations);
    formData.append('sanitization_mode', sanitizationMode);
    if (retrievalMode) formData.append('retrieval_mode', retrievalMode);

    fetch(`${API_BASE_URL}/tearline/upload/stream`, {
      method: 'POST',
      headers,
      body: formData,
    })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const readStream = () => {
        reader.read().then(({ done, value }) => {
          if (done) return;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          lines.forEach(line => {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                if (onProgress) onProgress(data);
                if (data.status === 'completed' || data.status === 'max_iterations_reached') {
                  resolve(data.final_result);
                } else if (data.status === 'error' || data.error) {
                  reject(new Error(data.message || data.error || 'Upload tearline failed'));
                }
              } catch (parseError) {
                console.error('Failed to parse upload stream data:', parseError);
              }
            }
          });
          readStream();
        }).catch(reject);
      };
      readStream();
    })
    .catch(reject);
  });
};

// Process PDF for FOIA with initial redactions
export const processPDFForFOIA = async (pdfFile, agency = "", requesterInterest = "") => {
  const formData = new FormData();
  formData.append('file', pdfFile);
  formData.append('agency', agency);
  formData.append('requester_interest', requesterInterest);

  const response = await api.post('/foia/process-pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  });
  
  return response.data;
};

// Generate redacted PDF with modified redactions
export const generateRedactedPDF = async (options) => {
  const {
    pdf_file,
    redactions = [],
    agency = "",
    requester_interest = ""
  } = options;

  const formData = new FormData();
  if (pdf_file) {
    formData.append('file', pdf_file);
  }
  formData.append('redactions', JSON.stringify(redactions));
  formData.append('agency', agency);
  formData.append('requester_interest', requester_interest);

  const response = await api.post('/foia/generate-redacted-pdf', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    responseType: 'blob' // Expecting PDF binary data
  });
  
  return response;
};

// FOIA Response Letter Generation
export const generateFOIALetter = async (data) => {
  const response = await api.post('/foia/response-letter', data);
  return response.data;
};

// ==================== Query History APIs ====================

// Get list of queries with optional filtering
export const getQueryHistory = async (options = {}) => {
  const {
    query_type = null,
    status = null,
    limit = 50,
    offset = 0,
    user_id = 'default'
  } = options;

  const params = new URLSearchParams();
  if (query_type) params.append('query_type', query_type);
  if (status) params.append('status', status);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  params.append('user_id', user_id);

  const response = await api.get(`/queries?${params.toString()}`);
  return response.data;
};

// Get a specific query by ID
export const getQuery = async (queryId) => {
  const response = await api.get(`/queries/${queryId}`);
  return response.data;
};

// Create a new query (starts async processing)
export const createQuery = async (queryType, inputText, inputParams = {}, userId = 'default') => {
  const response = await api.post('/queries', {
    query_type: queryType,
    input_text: inputText,
    input_params: inputParams,
    user_id: userId
  });
  return response.data;
};

// Delete a query from history
export const deleteQuery = async (queryId) => {
  const response = await api.delete(`/queries/${queryId}`);
  return response.data;
};

// Get query status (for polling)
export const getQueryStatus = async (queryId) => {
  const response = await api.get(`/queries/${queryId}/status`);
  return response.data;
};

// Rerun a previous query
export const rerunQuery = async (queryId) => {
  const response = await api.post(`/queries/${queryId}/rerun`);
  return response.data;
};

// Export query history
export const exportQueryHistory = async (options = {}) => {
  const {
    query_type = null,
    status = null,
    user_id = null,
    limit = 1000
  } = options;

  const params = new URLSearchParams();
  if (query_type) params.append('query_type', query_type);
  if (status) params.append('status', status);
  if (user_id) params.append('user_id', user_id);
  params.append('limit', limit.toString());

  const response = await api.get(`/queries/export?${params.toString()}`);
  return response.data;
};

// Get query history stats
export const getQueryHistoryStats = async (userId = null) => {
  const params = userId ? `?user_id=${userId}` : '';
  const response = await api.get(`/queries/stats${params}`);
  return response.data;
};

// Cleanup old queries
export const cleanupQueryHistory = async (daysOld = 30, userId = 'default') => {
  const response = await api.post('/queries/cleanup', {
    days_old: daysOld,
    user_id: userId
  });
  return response.data;
};

// ==================== Agentic SCG Search APIs ====================

// Agentic multi-strategy SCG rule search with iterative refinement
export const agenticSCGSearch = async (query, options = {}) => {
  const {
    target_classification = null,
    context_agency = null,
    context_domain = null,
    top_k = 20
  } = options;

  const response = await api.post('/scg/agentic-search', {
    query,
    target_classification,
    context_agency,
    context_domain,
    top_k
  });
  return response.data;
};

// Get SCG graph data for ReactFlow visualization
export const getSCGGraphData = async (triggeredRuleIds, targetClassification, targetCaveats = [], targetReleasability = []) => {
  const response = await api.post('/scg/graph-data', {
    triggered_rule_ids: triggeredRuleIds,
    target_classification: targetClassification,
    target_caveats: targetCaveats,
    target_releasability: targetReleasability
  });
  return response.data;
};

// Compare classification across all retrieval modes
export const classifyCompare = async (text, useScgRules = true) => {
  const response = await api.post('/classify/compare', { text, use_scg_rules: useScgRules });
  return response.data;
};

// Export classification result as PDF report
export const exportClassificationReport = async (result) => {
  const response = await api.post('/classify/export-report', result, { responseType: 'blob' });
  return response.data;
};

// ==================== Ask the SCG APIs ====================

// Ask a natural-language question against the SCG knowledge base
export const askSCG = async (question, topK = 10) => {
  const response = await api.post('/scg/ask', { question, top_k: topK });
  return response.data;
};

// Tearline What-If Analysis — toggle individual changes and reclassify
export const tearlineWhatIf = async (originalText, finalText, changes, disabledIndices) => {
  const response = await api.post('/tearline/what-if', {
    original_text: originalText,
    final_text: finalText,
    changes,
    disabled_indices: disabledIndices,
  });
  return response.data;
};

// ==================== Declassification Review APIs ====================

// Review a document for potential declassification
export const reviewForDeclass = async (text, originalClassification, originalDeclassDate, originalExemption) => {
  const response = await api.post('/declass/review', {
    text,
    original_classification: originalClassification,
    original_declass_date: originalDeclassDate,
    original_exemption: originalExemption,
  });
  return response.data;
};

// ==================== Feedback APIs ====================

// Submit one or more feedback entries (classification, tearline, FOIA)
export const submitFeedback = async (entries) => {
  const response = await api.post('/feedback', { entries });
  return response.data;
};

// Query feedback with filters (including review_status)
export const queryFeedback = async (pipeline, { documentHash, decision, reviewStatus, limit = 100, offset = 0 } = {}) => {
  const params = { pipeline, limit, offset };
  if (documentHash) params.document_hash = documentHash;
  if (decision) params.decision = decision;
  if (reviewStatus) params.review_status = reviewStatus;
  const response = await api.get('/feedback', { params });
  return response.data;
};

// Get aggregated feedback stats
export const getFeedbackStats = async (pipeline = null) => {
  const params = pipeline ? { pipeline } : {};
  const response = await api.get('/feedback/stats', { params });
  return response.data;
};

// Admin: approve or reject a feedback entry
export const reviewFeedback = async (feedbackId, pipeline, reviewStatus, reviewedBy = 'admin') => {
  const response = await api.post(`/feedback/${feedbackId}/review?pipeline=${pipeline}`, {
    review_status: reviewStatus,
    reviewed_by: reviewedBy,
  });
  return response.data;
};

// Admin: bulk approve/reject
export const bulkReviewFeedback = async (feedbackIds, pipeline, reviewStatus) => {
  const response = await api.post('/feedback/bulk-review', {
    feedback_ids: feedbackIds,
    pipeline_type: pipeline,
    review_status: reviewStatus,
  });
  return response.data;
};

// Admin: LLM synthesizes approved corrections into curated rules
export const generateFeedbackRules = async (pipeline) => {
  const response = await api.post('/feedback/generate-rules', { pipeline_type: pipeline });
  return response.data;
};

// Get curated rules
export const getCuratedRules = async (pipeline = null, activeOnly = true) => {
  const params = { active_only: activeOnly };
  if (pipeline) params.pipeline = pipeline;
  const response = await api.get('/feedback/rules', { params });
  return response.data;
};

// Save curated rules
export const saveCuratedRules = async (rules, createdBy = 'admin') => {
  const response = await api.post('/feedback/rules', { rules, created_by: createdBy });
  return response.data;
};

// Toggle curated rule active/inactive
export const toggleCuratedRule = async (ruleId, active) => {
  const response = await api.put(`/feedback/rules/${ruleId}?active=${active}`);
  return response.data;
};

// Delete curated rule
export const deleteCuratedRule = async (ruleId) => {
  const response = await api.delete(`/feedback/rules/${ruleId}`);
  return response.data;
};

// ================= Provenance Graph =================
export const getProvenanceStats = async () => {
  const response = await api.get('/provenance/stats');
  return response.data;
};

export const listProvenanceDecisions = async ({
  type, userId, since, until, limit = 50, offset = 0,
} = {}) => {
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  if (userId) params.append('user_id', userId);
  if (since) params.append('since', since);
  if (until) params.append('until', until);
  params.append('limit', limit);
  params.append('offset', offset);
  const response = await api.get(`/provenance/decisions?${params.toString()}`);
  return response.data;
};

export const getProvenanceDecision = async (decisionId) => {
  const response = await api.get(`/provenance/decisions/${encodeURIComponent(decisionId)}`);
  return response.data;
};

export const getProvenanceNeighbors = async (decisionId) => {
  const response = await api.get(`/provenance/decisions/${encodeURIComponent(decisionId)}/neighbors`);
  return response.data;
};

export const getRuleUsage = async (ruleId) => {
  const response = await api.get(`/provenance/rules/${encodeURIComponent(ruleId)}/usage`);
  return response.data;
};

export const getRuleTrustScoresBulk = async (ruleIds) => {
  if (!Array.isArray(ruleIds) || ruleIds.length === 0) {
    return { scores: {}, count: 0 };
  }
  const response = await api.post('/provenance/rules/trust-scores', { rule_ids: ruleIds });
  return response.data;
};

// ================= Provenance Week 3: Deprecation + Cascades =================
export const simulateRuleDeprecation = async (ruleIds) => {
  const response = await api.post('/provenance/simulate/rule-deprecation', { rule_ids: ruleIds });
  return response.data;
};

export const getCascadeForDocument = async (documentId, maxDepth = 5) => {
  const response = await api.get(`/provenance/cascades/document/${encodeURIComponent(documentId)}?max_depth=${maxDepth}`);
  return response.data;
};

export const getCascadeForDecision = async (decisionId, maxDepth = 5) => {
  const response = await api.get(`/provenance/cascades/decision/${encodeURIComponent(decisionId)}?max_depth=${maxDepth}`);
  return response.data;
};

// ================= Provenance Week 4: Analytics + Export =================
export const getOverrideAnalytics = async ({ since, until } = {}) => {
  const params = new URLSearchParams();
  if (since) params.append('since', since);
  if (until) params.append('until', until);
  const response = await api.get(`/provenance/overrides/analytics?${params.toString()}`);
  return response.data;
};

export const exportComplianceArtifact = async (decisionId) => {
  const response = await api.get(`/provenance/decisions/${encodeURIComponent(decisionId)}/export`);
  return response.data;
};

export default api;
// ============================================================================
// Preserved from main (ac5cf462 / SB9): bulk SCG ingestion + cross-SCG
// conflict detector. Restored after merging origin/old-machine into main on
// 2026-05-08 — these endpoints don't exist on old-machine but are live on
// main and prod, so the frontend functions need to keep matching them.
// ============================================================================

// SCG Bulk Upload — durable batch ingestion via Celery
export const bulkUploadSCG = async (filesArray, llmConcurrency = null) => {
  const formData = new FormData();
  filesArray.forEach((f) => formData.append('files', f));
  if (llmConcurrency != null) {
    formData.append('llm_concurrency', String(llmConcurrency));
  }
  const response = await api.post('/scg/bulk/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const getBulkSCGJob = async (batchId) => {
  const response = await api.get(`/scg/bulk/jobs/${batchId}`);
  return response.data;
};

export const getBulkSCGRejectedRules = async (batchId, limit = 2000) => {
  const response = await api.get(`/scg/bulk/jobs/${batchId}/rejected?limit=${limit}`);
  return response.data;
};

export const listBulkSCGJobs = async ({ limit = 50, offset = 0, activeOnly = false } = {}) => {
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  params.append('offset', String(offset));
  if (activeOnly) params.append('active_only', 'true');
  const response = await api.get(`/scg/bulk/jobs?${params.toString()}`);
  return response.data;
};

export const cancelBulkSCGJob = async (batchId) => {
  const response = await api.delete(`/scg/bulk/jobs/${batchId}`);
  return response.data;
};

export const reloadSCGRules = async () => {
  const response = await api.post('/scg/reload');
  return response.data;
};

// Cross-SCG Conflict Detector
export const getConflictStats = async () => {
  const response = await api.get('/scg/conflicts/stats');
  return response.data;
};

export const listSCGConflicts = async ({ verdict, severity, source_scg, reviewed, status, limit = 200, offset = 0 } = {}) => {
  const params = new URLSearchParams();
  if (verdict) params.append('verdict', verdict);
  if (severity) params.append('severity', severity);
  if (source_scg) params.append('source_scg', source_scg);
  if (reviewed) params.append('reviewed', reviewed);
  if (status) params.append('status', status);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  const response = await api.get(`/scg/conflicts?${params.toString()}`);
  return response.data;
};

export const getSCGConflict = async (conflictId) => {
  const response = await api.get(`/scg/conflicts/${conflictId}`);
  return response.data;
};

export const startSCGConflictScan = async ({ extract_only = false, max_pairs_to_judge = 200, generate_probes_top_n = 10 } = {}) => {
  const response = await api.post('/scg/conflicts/scan', {
    extract_only, max_pairs_to_judge, generate_probes_top_n,
  });
  return response.data;
};

export const getSCGConflictScanStatus = async (jobId) => {
  const response = await api.get(`/scg/conflicts/scan/${jobId}`);
  return response.data;
};

export const reviewSCGConflict = async (conflictId, review) => {
  const response = await api.post(`/scg/conflicts/${conflictId}/review`, { review });
  return response.data;
};

export const resolveSCGConflict = async (conflictId, resolved = true) => {
  const response = await api.post(`/scg/conflicts/${conflictId}/resolve`, { resolved });
  return response.data;
};

export const generateSCGConflictProbe = async (conflictId) => {
  const response = await api.post(`/scg/conflicts/${conflictId}/probe`);
  return response.data;
};
